// sales-req-v1
import { useEffect, useState } from "react";
import { ClipboardList, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const NUM = [
  { key: "plot_area", label: "Plot area", unit: "sq ft" },
  { key: "built_up_area", label: "Built-up area", unit: "sq ft" },
  { key: "floors", label: "Floors", unit: "" },
  { key: "bedrooms", label: "Bedrooms", unit: "" },
];
export const CHOICE = [
  { key: "site_status", label: "Site status", options: ["Vacant plot", "Existing building", "Under construction", "Not finalised"] },
  { key: "design_status", label: "Design", options: ["Client has design", "We prepare design", "Design in progress"] },
  { key: "finishing", label: "Finishing level", options: ["Basic", "Standard", "Premium", "Luxury"] },
];
const EXTRA = [{ key: "start_by", label: "Wants to start by" }, { key: "parking", label: "Parking / extras" }];
const SELECT = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

/** The client's brief, collected on the lead. Areas and floors carry over to the proposal as its measurements. */
export function RequirementsCard({ lead, onSaved }) {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const saved = lead.requirements || {};
  useEffect(() => { setV(Object.fromEntries(Object.entries(lead.requirements || {}).map(([k, x]) => [k, String(x)]))); }, [lead.id, lead.requirements]);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const dirty = JSON.stringify(Object.fromEntries(Object.entries(v).filter(([, x]) => x !== ""))) !== JSON.stringify(Object.fromEntries(Object.entries(saved).map(([k, x]) => [k, String(x)])));
  const filled = Object.keys(saved).length;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.patch(`/pipeline/leads/${lead.id}`, { requirements: Object.fromEntries(Object.entries(v).filter(([, x]) => String(x).trim() !== "")) }); toast.success("Requirements saved"); onSaved(data); }
    catch (ex) { toast.error(getErrorMessage(ex), { duration: 8000 }); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="lead-requirements">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><ClipboardList className="h-3.5 w-3.5" /> Client requirements</span>
        <span className="text-[11px] text-muted-foreground">{filled ? `${filled} filled` : "Not collected yet"}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {NUM.map((f) => (
          <div key={f.key}><Label className="text-xs">{f.label}{f.unit ? ` (${f.unit})` : ""}</Label>
            <Input type="number" min="0" value={v[f.key] ?? ""} onChange={set(f.key)} data-testid={`req-${f.key}`} /></div>
        ))}
        {CHOICE.map((f) => (
          <div key={f.key} className={f.key === "finishing" ? "" : "col-span-2"}><Label className="text-xs">{f.label}</Label>
            <select className={SELECT} value={v[f.key] ?? ""} onChange={set(f.key)} data-testid={`req-${f.key}`}>
              <option value="">Select…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
        ))}
        {EXTRA.map((f) => (
          <div key={f.key} className={f.key === "start_by" ? "" : "col-span-2"}><Label className="text-xs">{f.label}</Label>
            <Input value={v[f.key] ?? ""} onChange={set(f.key)} data-testid={`req-${f.key}`} /></div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">Plot area, built-up area, floors and bedrooms are copied to the proposal when you make it.</p>
      <Button className="mt-3 w-full" size="sm" disabled={busy || !dirty} onClick={save} data-testid="req-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save requirements</Button>
    </div>
  );
}
